"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, Loader, CircleCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
const inputClasses = "flex h-10 w-full rounded-none border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors";
const textareaClasses = "flex min-h-[80px] w-full rounded-none border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors";
const selectClasses = "flex h-10 w-full items-center justify-between rounded-none border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors appearance-none";
export default function AbileneOfficeHub() {
  return <section id="abilene-office-hub" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {}
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground tracking-tight">
                Visit Our Abilene Hub
              </h2>
              <p className="text-muted-foreground text-lg max-w-md">
                Whether you're an owner looking for management solutions or a tenant searching for your next home, our team is here to help.
              </p>
            </div>

            <Card className="border-border/50 shadow-lg bg-card/50 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-xl font-serif">Contact Information</CardTitle>
                <CardDescription>Reach out to us directly or visit our office.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="w-5 h-5 text-primary mt-1 shrink-0" />
                  <div>
                    <p className="font-medium text-foreground">Address</p>
                    <p className="text-muted-foreground">Abilene, Texas</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <Phone className="w-5 h-5 text-primary mt-1 shrink-0" />
                  <div>
                    <p className="font-medium text-foreground">Phone</p>
                    <Link to="tel:3255551234" className="text-muted-foreground hover:text-primary transition-colors">(325) 529-3020</Link>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Mail className="w-5 h-5 text-primary mt-1 shrink-0" />
                  <div>
                    <p className="font-medium text-foreground">Email</p>
                    <Link to="mailto:info@tailletrealestate.com" className="text-muted-foreground hover:text-primary transition-colors">taillet.realestate@gmail.com</Link>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Clock className="w-5 h-5 text-primary mt-1 shrink-0" />
                  <div>
                    <p className="font-medium text-foreground">Office Hours</p>
                    <p className="text-muted-foreground">24 hours, 7 days a week</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {}
          <Separator className="lg:hidden bg-slate-200 dark:bg-slate-800" />

          {}
          <div className="bg-card rounded-xl border border-border shadow-sm p-6 md:p-8">
            <div className="mb-6">
              <h3 className="text-2xl font-serif font-semibold text-foreground mb-2">Abilene Office Hub Inquiry</h3>
              <p className="text-muted-foreground text-sm">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>
            </div>

            <FormProvider formId="office_hub_inquiry_reach_out" sectionName="abilene-office-hub" defaultValues={{
            name: "",
            email: "",
            phone: "",
            company_name: "",
            office_size: "",
            lease_term: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            office_size: {
              required: true
            },
            lease_term: {
              required: true
            }
          }} submitText="Send Inquiry" successMessage="Thank you for your inquiry. We will contact you shortly." errorMessage="There was a problem sending your inquiry. Please try again." className="w-full">
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-300">
        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-2">
          <CircleCheck className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-xl font-semibold text-foreground">Inquiry Sent!</h3>
        <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel>Your Name <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <input {...field} type="text" placeholder="John Doe" className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <input {...field} type="email" placeholder="john@example.com" className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <input {...field} type="tel" placeholder="(555) 123-4567" className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem>
              <FormLabel>Company / Organization</FormLabel>
              <FormControl>
                <input {...field} type="text" placeholder="Acme Corp." className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="office_size" render={({
      field
    }) => <FormItem>
            <FormLabel>Desired Office Size <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <div className="relative">
                <select {...field} className={selectClasses}>
                  <option value="" disabled>Select size</option>
                  <option value="Under 500 sq ft">Under 500 sq ft</option>
                  <option value="500 – 1,000 sq ft">500 – 1,000 sq ft</option>
                  <option value="1,001 – 2,500 sq ft">1,001 – 2,500 sq ft</option>
                  <option value="2,501 – 5,000 sq ft">2,501 – 5,000 sq ft</option>
                  <option value="5,001+ sq ft">5,001+ sq ft</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                  <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="lease_term" render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel>Preferred Lease Term <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-6">
                {["Short-term (up to 6 months)", "Mid-term (6–12 months)", "Long-term (12+ months)"].map((option, i) => <label key={option} data-index={i} className="flex items-center space-x-2 cursor-pointer">
                    <input type="radio" value={option} checked={field.value === option} onChange={field.onChange} className="aspect-square h-4 w-4 rounded-full border border-primary text-primary ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 accent-primary" />
                    <span className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      {option}
                    </span>
                  </label>)}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel>Additional Details / Questions</FormLabel>
            <FormControl>
              <textarea {...field} placeholder="Tell us more about your needs..." className={textareaClasses} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[150px]">
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Sending...
          </> : "Send Inquiry"}
      </Button>
    </Form>;
}